import * as React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router';
import { brand } from '../shared-theme/themePrimitives';

export default function LoginPage() {
  const { login, isLoading, error, clearError, isAuthenticated } = useAuth();
  const { t, direction } = useLanguage();
  const navigate = useNavigate();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState<{
    email?: string;
    password?: string;
  }>({});

  const isRtl = direction === 'rtl';

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  React.useEffect(() => {
    // Clear error when component unmounts
    return () => {
      clearError();
    };
  }, [clearError]);

  const validateForm = (): boolean => {
    const errors: { email?: string; password?: string } = {};

    if (!email) {
      errors.email = t('auth.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = t('auth.invalidEmail');
    }

    if (!password) {
      errors.password = t('auth.passwordRequired');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await login({ email, password });
      navigate('/');
    } catch (error) {
      // Error is handled by AuthContext
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (validationErrors.email) {
      setValidationErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (validationErrors.password) {
      setValidationErrors((prev) => ({ ...prev, password: undefined }));
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        overflow: 'hidden',
      }}
    >
      {/* Side Panel - Logo Section */}
      <Box
        sx={{
          flex: { xs: '0 0 auto', md: '0 0 30%' },
          minHeight: { xs: '200px', md: '100vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${brand[700]} 0%, ${brand[500]} 50%, ${brand[600]} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '80%',
            height: '80%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none',
          },
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            zIndex: 1,
            px: 4,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              color: 'white',
              letterSpacing: '1.5px',
              fontSize: { xs: '2rem', md: '2.5rem' },
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              mb: 2,
            }}
          >
            ALFAKHAMA
          </Typography>
          <Box
            sx={{
              width: '60px',
              height: '3px',
              background: 'linear-gradient(90deg, transparent, white, transparent)',
              margin: '0 auto',
              borderRadius: '2px',
            }}
          />
        </Box>
      </Box>

      {/* Main Content - Login Form Section */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: (theme) => theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          padding: { xs: 3, sm: 4, md: 6 },
          position: 'relative',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 480,
            padding: { xs: 4, sm: 6 },
            borderRadius: 3,
            background: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: (theme) => `1px solid ${theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(2, 139, 203, 0.1)'}`,
            boxShadow: '0 8px 32px rgba(2, 139, 203, 0.1)',
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {/* Welcome Text */}
            <Box sx={{ textAlign: 'center', mb: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: brand[400],
                  mb: 1,
                  letterSpacing: '-0.5px',
                }}
              >
                {t('auth.login')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.95rem',
                }}
              >
                {t('auth.signInToContinue')}
              </Typography>
            </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={clearError}>
              {error}
            </Alert>
          )}

          {/* Email Field */}
          <TextField
            fullWidth
            label={t('auth.email')}
            type="email"
            value={email}
            onChange={handleEmailChange}
            error={!!validationErrors.email}
            helperText={validationErrors.email}
            disabled={isLoading}
            autoComplete="email"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: brand[400] }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(255, 255, 255, 0.8)',
                borderRadius: 2,
                '& input': {
                  color: 'rgba(0, 0, 0, 0.87)',
                },
                '& fieldset': {
                  borderColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(2, 139, 203, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: brand[400],
                },
                '&.Mui-focused fieldset': {
                  borderColor: brand[400],
                  borderWidth: '2px',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: brand[400],
              },
              '& .MuiInputLabel-root': {
                textAlign: isRtl ? 'right' : 'left',
                right: isRtl ? 24 : 'auto',
                left: isRtl ? 'auto' : 0,
              },
            }}
          />

          {/* Password Field */}
          <TextField
            fullWidth
            label={t('auth.password')}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordChange}
            error={!!validationErrors.password}
            helperText={validationErrors.password}
            disabled={isLoading}
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: brand[400]}} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    disabled={isLoading}
                    sx={{ backgroundColor: 'transparent !important', border: 0, '&:hover': { backgroundColor: 'transparent !important' }, '&:focus': { backgroundColor: 'transparent !important' }, '&:active': { backgroundColor: 'transparent !important' }, color: 'rgba(0, 0, 0, 0.54)' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(255, 255, 255, 0.8)',
                borderRadius: 2,
                '& input': {
                  color: 'rgba(0, 0, 0, 0.87)',
                },
                '& fieldset': {
                  borderColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(2, 139, 203, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: brand[400],
                },
                '&.Mui-focused fieldset': {
                  borderColor: brand[400],
                  borderWidth: '2px',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: brand[400],
              },
              '& .MuiInputLabel-root': {
                textAlign: isRtl ? 'right' : 'left',
                right: isRtl ? 24 : 'auto',
                left: isRtl ? 'auto' : 0,
              },
            }}
          />

          {/* Login Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{
              mt: 2,
              py: 1.75,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              background: `linear-gradient(135deg, ${brand[400]} 0%, ${brand[500]} 100%)`,
              boxShadow: '0 4px 20px rgba(2, 139, 203, 0.3)',
              border: 0,
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: `linear-gradient(135deg, ${brand[500]} 0%, ${brand[600]} 100%)`,
                boxShadow: '0 6px 24px rgba(2, 139, 203, 0.4)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              '&:disabled': {
                background: brand[200],
                boxShadow: 'none',
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              t('auth.login')
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
    </Box>
  );
}

